import {
  collection,
  doc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  updateDoc,
  Timestamp,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "./init";
import { convertTimestampToDate } from "./firestore";
import { FailedPurchase } from "@/types/failedPurchase";

/**
 * 失敗購買記錄列表查詢選項
 */
export interface FailedPurchaseListOptions {
  filters?: {
    status?: FailedPurchase["status"];
    type?: FailedPurchase["type"];
    platform?: FailedPurchase["platform"];
    failure_stage?: FailedPurchase["failure_stage"];
  };
  limit?: number;
  startAfter?: QueryDocumentSnapshot<DocumentData>;
}

/**
 * 失敗購買記錄列表查詢結果
 */
export interface FailedPurchaseListResult {
  failedPurchases: FailedPurchase[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

/**
 * 獲取失敗購買記錄（支援分頁）
 */
export async function getAllFailedPurchases(
  options: FailedPurchaseListOptions = {}
): Promise<FailedPurchaseListResult> {
  try {
    const failedPurchasesCollection = collection(db, "failed_purchases");
    const constraints: QueryConstraint[] = [];
    const filters = options.filters || {};
    const pageSize = options.limit || 50;

    // 狀態篩選
    if (filters.status) {
      constraints.push(where("status", "==", filters.status));
    }

    // 類型篩選
    if (filters.type) {
      constraints.push(where("type", "==", filters.type));
    }

    // 平台篩選
    if (filters.platform) {
      constraints.push(where("platform", "==", filters.platform));
    }

    // 失敗階段篩選
    if (filters.failure_stage) {
      constraints.push(where("failure_stage", "==", filters.failure_stage));
    }

    // 排序
    constraints.push(orderBy("timestamp", "desc"));

    // 分頁
    if (options.startAfter) {
      constraints.push(startAfter(options.startAfter));
    }

    // 限制數量（多取一筆以判斷是否有更多資料）
    constraints.push(limit(pageSize + 1));

    const q = query(failedPurchasesCollection, ...constraints);
    const querySnapshot = await getDocs(q);

    const docs = querySnapshot.docs;
    const hasMore = docs.length > pageSize;
    const failedPurchases = (hasMore ? docs.slice(0, pageSize) : docs).map(
      (doc) => ({
        id: doc.id,
        ...convertTimestampToDate(doc.data()),
      })
    ) as FailedPurchase[];

    return {
      failedPurchases,
      lastDoc: hasMore ? docs[pageSize - 1] : null,
      hasMore,
    };
  } catch (error) {
    console.error("Error getting failed purchases:", error);
    // 如果查詢失敗（可能是因為沒有索引），返回空結果
    if (error instanceof Error && error.message.includes("index")) {
      console.warn("Index may not exist for failed_purchases query");
      return {
        failedPurchases: [],
        lastDoc: null,
        hasMore: false,
      };
    }
    throw error;
  }
}

/**
 * 匯出用：依目前篩選條件抓取全部失敗購買記錄
 * 以分頁迴圈抓取，預設上限 10,000 筆
 */
export async function fetchAllFailedPurchasesForExport(
  filters: FailedPurchaseListOptions["filters"] = {},
  options: { batchSize?: number; maxRecords?: number } = {}
): Promise<{ failedPurchases: FailedPurchase[]; truncated: boolean; total: number }> {
  const batchSize = options.batchSize ?? 500;
  const maxRecords = options.maxRecords ?? 10000;

  const all: FailedPurchase[] = [];
  let cursor: QueryDocumentSnapshot<DocumentData> | undefined = undefined;
  let hasMore = true;

  while (hasMore && all.length < maxRecords) {
    const limit = Math.min(batchSize, maxRecords - all.length);
    const result = await getAllFailedPurchases({
      filters,
      limit,
      startAfter: cursor,
    });
    all.push(...result.failedPurchases);
    hasMore = result.hasMore;
    cursor = result.lastDoc ?? undefined;
    if (!cursor) break;
  }

  return {
    failedPurchases: all,
    truncated: hasMore && all.length >= maxRecords,
    total: all.length,
  };
}

/**
 * 搜尋失敗購買記錄（根據 transaction_id 或 user_id）
 */
export async function searchFailedPurchases(
  searchQuery: string,
  limitCount: number = 100
): Promise<FailedPurchase[]> {
  if (!searchQuery.trim()) {
    return [];
  }

  try {
    const failedPurchasesCollection = collection(db, "failed_purchases");
    const searchLower = searchQuery.toLowerCase();

    // 由於 Firestore 不支援全文搜尋，我們需要獲取所有記錄並在客戶端過濾
    // 或者使用 transaction_id 和 user_id 的精確匹配
    const constraints: QueryConstraint[] = [
      orderBy("timestamp", "desc"),
      limit(limitCount * 2), // 獲取更多記錄以便過濾
    ];

    const q = query(failedPurchasesCollection, ...constraints);
    const querySnapshot = await getDocs(q);

    // 客戶端過濾
    const results = querySnapshot.docs
      .map((doc) => ({
        id: doc.id,
        ...convertTimestampToDate(doc.data()),
      }))
      .filter((failedPurchase) => {
        const transactionId = failedPurchase.transaction_id?.toLowerCase() || "";
        const userId = failedPurchase.user_id?.toLowerCase() || "";
        const errorCode = failedPurchase.error_code?.toLowerCase() || "";
        const errorMessage = failedPurchase.error_message?.toLowerCase() || "";

        return (
          transactionId.includes(searchLower) ||
          userId.includes(searchLower) ||
          errorCode.includes(searchLower) ||
          errorMessage.includes(searchLower)
        );
      })
      .slice(0, limitCount) as FailedPurchase[];

    return results;
  } catch (error) {
    console.error("Error searching failed purchases:", error);
    throw error;
  }
}

/**
 * 更新失敗購買記錄的狀態
 */
export async function updateFailedPurchaseStatus(
  transactionId: string,
  status: FailedPurchase["status"],
  resolutionNotes?: string,
  resolvedBy?: string
): Promise<void> {
  try {
    const failedPurchaseRef = doc(db, "failed_purchases", transactionId);
    const updateData: {
      status: FailedPurchase["status"];
      resolved_at?: Timestamp;
      resolved_by?: string;
      resolution_notes?: string;
    } = {
      status,
    };

    if (status === "resolved" || status === "rejected") {
      updateData.resolved_at = Timestamp.now();
      if (resolvedBy) {
        updateData.resolved_by = resolvedBy;
      }
    }

    if (resolutionNotes) {
      updateData.resolution_notes = resolutionNotes;
    }

    await updateDoc(failedPurchaseRef, updateData);
  } catch (error) {
    console.error("Error updating failed purchase status:", error);
    throw error;
  }
}
